import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { DatePicker, Select, TimeRangePickerProps } from 'antd';

import dayjs from 'dayjs';
import eventService from 'services/event';
import { IFilter } from 'services/marketplace/types';

import styles from './styles.module.css';

type FitlerProps = {
  filters: IFilter;
  setFilters: Dispatch<SetStateAction<IFilter>>;
};

const { RangePicker } = DatePicker;

const rangePresets: TimeRangePickerProps['presets'] = [
  { label: 'Hoje', value: [dayjs(), dayjs()] },
  { label: 'Amanhã', value: [dayjs().add(1, 'd'), dayjs()] },
  { label: 'Esta semana', value: [dayjs().endOf('week'), dayjs()] },
  {
    label: 'Próxima semana',
    value: [dayjs().add(1, 'week').endOf('week'), dayjs().add(1, 'week').startOf('week')]
  },
  { label: 'Este mês', value: [dayjs().endOf('month'), dayjs()] }
];

const Filters = ({ filters, setFilters }: FitlerProps) => {
  const [dates, setDates] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
    filters.startDate ? dayjs(filters.startDate) : null,
    filters.endDate ? dayjs(filters.endDate) : null
  ]);
  const [categories, setCategories] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (filters.startDate === undefined && filters.endDate === undefined) {
      setDates([null, null]);
    }
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    setLoading(true);
    const sub = eventService.listPublicCategories().subscribe(data => {
      const categoryList = data.map(category => ({ label: category.title, value: category.id }));
      setCategories(categoryList);
      setLoading(false);
    });

    return () => {
      sub.unsubscribe();
    };
  }, []);

  const handleCategoryChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      categoryId: value,
      page: 1
    }));
  };

  const handleTypeChange = (type: 'online' | 'presential') => {
    setFilters(prev => ({
      ...prev,
      type,
      page: 1
    }));
  };

  const handleDateChange = (date: [dayjs.Dayjs, dayjs.Dayjs]) => {
    setDates(date);

    if (!date) {
      setFilters(prev => ({
        ...prev,
        startDate: undefined,
        endDate: undefined,
        page: 1
      }));
      return;
    }

    if (date[0]) {
      const startDate = date[0].format('YYYY-MM-DD');
      const endDate = date[1] ? date[1].format('YYYY-MM-DD') : undefined;
      setFilters(prev => ({
        ...prev,
        startDate,
        endDate,
        page: 1
      }));
    }
  };

  return (
    <>
      <RangePicker
        id='date-range'
        minDate={dayjs()}
        format='DD/MM/YYYY'
        allowEmpty={[false, true]}
        presets={rangePresets}
        onChange={handleDateChange}
        className={` ${styles.input} ${styles.rangeDatePicker}`}
        value={dates}
      />

      <Select
        id='category-select'
        placeholder='Categoria'
        options={categories}
        loading={loading}
        onChange={handleCategoryChange}
        value={filters.categoryId}
        popupMatchSelectWidth={false}
        className={styles.input}
        allowClear
      />

      <Select
        id='type-select'
        placeholder='Tipo do evento'
        onChange={handleTypeChange}
        value={filters.type}
        className={styles.input}
        options={[
          { label: 'Presencial', value: 'presential' },
          { label: 'Online', value: 'online' }
        ]}
        popupMatchSelectWidth={false}
        allowClear
      />
    </>
  );
};

export default Filters;
